/**
 * Hovering a colour dot previews that colour's photo in the relevant main image,
 * reverting on mouseout. Works in two contexts, both of which already carry the
 * preview image in data-swatch-preview - this just consumes it:
 * - Collection cards (.card-swatches__item) - swaps the card's own thumbnail.
 * - Shopify's own variant picker (.swatch-input__label) on products whose colour is a real
 *   variant option, so those behave the same as the sibling-linked ones. The preview URL is
 *   emitted by swatch-input.liquid when a preview_image is passed.
 * - The product page (.product-swatches__item, the 'large' size in card-swatches.liquid) -
 *   swaps the currently-active slide in the media gallery. Only a lightweight src swap on
 *   the visible image, not a real slide change - doesn't touch the slider position,
 *   thumbnails, or zoom/modal, same contained scope as the collection-card version.
 *
 * Delegated on document rather than bound per-swatch, so it keeps working after facets.js
 * re-renders the collection grid on filter/sort changes.
 *
 * Registered in the CAPTURE phase deliberately - confirmed 2026-09-19 that something
 * else on the page (likely the RestockRocket app embed, same one that had to be worked
 * around for clicks) calls stopPropagation on these events during the bubble phase, so a
 * normal (bubble-phase) document listener never saw them. A capture-phase listener runs
 * on the way down, before that interference happens, same fix shape as the click issue.
 */
document.addEventListener('mouseover', handlePreviewHover, true);
document.addEventListener('mouseout', handlePreviewHover, true);

function handlePreviewHover(event) {
  const item = event.target.closest(
    '.card-swatches__item, .product-swatches__item, .swatch-input__label'
  );
  if (!item) return;

  const img = findPreviewImage(item);
  if (!img) return;

  if (img.dataset.originalSrc === undefined) {
    img.dataset.originalSrc = img.getAttribute('src') || '';
    img.dataset.originalSrcset = img.getAttribute('srcset') || '';
    img.dataset.originalSizes = img.getAttribute('sizes') || '';
  }

  const previewUrl = item.getAttribute('data-swatch-preview');
  // Full responsive srcset from swatch-preview-attrs.liquid, so the browser picks a
  // resolution to suit the image's own sizes attribute. Labels that only carry a
  // single URL (swatch-input.liquid) fall back to src alone, as before.
  const previewSrcset = item.getAttribute('data-swatch-preview-srcset') || '';

  // Collection cards only: flags the card's .media while previewing, so the CSS can
  // zoom the preview in and keep Dawn's second-image-on-hover from covering it.
  const cardMedia = item.closest('.card-wrapper') ? img.closest('.media') : null;

  if (event.type === 'mouseover' && previewUrl) {
    // The card's sizes attribute describes the unzoomed slot, so the browser would
    // pick a file for that width and the CSS zoom would then stretch it soft. Ask
    // for one sized to the zoomed width instead. Set before srcset so the browser
    // selects against the new value.
    if (cardMedia && img.clientWidth) {
      img.setAttribute('sizes', `${Math.ceil(img.clientWidth * getPreviewZoom())}px`);
    }
    img.setAttribute('srcset', previewSrcset);
    img.setAttribute('src', previewUrl);
    cardMedia?.classList.add('media--swatch-preview');
  } else if (event.type === 'mouseout') {
    img.setAttribute('src', img.dataset.originalSrc);
    img.setAttribute('srcset', img.dataset.originalSrcset);
    if (cardMedia) img.setAttribute('sizes', img.dataset.originalSizes);
    cardMedia?.classList.remove('media--swatch-preview');
  }
}

// The "Hover photo zoom" theme setting, output as --swatch-preview-zoom in theme.liquid
// and used by the transform in component-card-swatches.css.
function getPreviewZoom() {
  const zoom = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--swatch-preview-zoom')
  );
  return zoom >= 1 ? zoom : 1;
}

function findPreviewImage(item) {
  const cardImg = item.closest('.card-wrapper')?.querySelector('.card__media .media img');
  if (cardImg) return cardImg;

  // Product page: only one media-gallery per page, swap the currently-visible slide's image.
  return document.querySelector('media-gallery .product__media-item.is-active .product__media img');
}
